import React, { useEffect, useState } from "react";
import { Modal, Button, Badge, Spinner, Nav } from "react-bootstrap";
import { FiShield, FiCheck, FiX, FiUser, FiClock, FiRefreshCw, FiLock, FiExternalLink } from "react-icons/fi";
import WebService from "../../Services/WebService";
import { toast } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

interface Props {
  show: boolean;
  onHide: () => void;
  onUpdateCount?: (count: number) => void;
}

export interface DocumentAccessRequestItem {
  id: number;
  uuid?: string;
  requester_id: number;
  requester_uuid?: string;
  owner_id: number;
  status: "PENDING" | "ACCEPTED" | "REJECTED" | "REVOKED";
  requested_at?: string;
  created_at?: string;
  responded_at?: string;
  updated_at?: string;
  user_type?: string;
  user_type_label?: string;
  is_mentor?: boolean;
  is_request_from_mentor?: boolean;
  requester?: {
    id: number;
    uuid?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    display_number?: string;
    profile_pic?: string | null;
    profile_picture?: string | null;
    profile_image_url?: string | null;
    is_verified?: boolean;
    user_verification_status?: string;
    user_type?: string;
    login_type?: string;
    account_type?: string;
  };
}

const DocumentAccessRequestsModal: React.FC<Props> = ({ show, onHide, onUpdateCount }) => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<DocumentAccessRequestItem[]>([]);
  const [activeTab, setActiveTab] = useState<"ALL" | "PENDING" | "ACCEPTED" | "REJECTED">("PENDING");
  const [actionLoading, setActionLoading] = useState<Record<number, boolean>>({});

  const navigate = useNavigate();
  const activeLoginTypes = useSelector((state: any) => state?.items?.storeActiveLoginType);

  const handleViewProfile = (item: DocumentAccessRequestItem) => {
    onHide();
    if (!item) return;
    const req = item.requester;
    const targetId =
      req?.uuid ||
      (item as any).requester_uuid ||
      item.uuid ||
      req?.id ||
      (item as any).requester_id ||
      null;

    if (!targetId) {
      toast.error("Requester details not available.");
      return;
    }

    const userTypeStr = (
      item.user_type ||
      req?.user_type ||
      req?.login_type ||
      req?.account_type ||
      ""
    ).toString().toLowerCase();

    const isMentor = Boolean(
      item.is_mentor ||
      item.is_request_from_mentor ||
      userTypeStr.includes("mentor")
    );

    if (isMentor) {
      navigate(`/mentor/mentor-connect/${targetId}`);
    } else if (window.location.pathname.startsWith("/mentor")) {
      navigate(`/mentor/customer/${targetId}`);
    } else {
      const loginType = activeLoginTypes?.loginType || "entrepreneur";
      const accountType = activeLoginTypes?.account_type || "investment";
      navigate(`/${loginType}/${accountType}/businesses/${targetId}`);
    }
  };

  const fetchRequests = () => {
    setLoading(true);
    WebService.getAPI({
      action: "document-access/received",
      isShowError: true,
    })
      .then((res: any) => {
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.info)
              ? res.info
              : Array.isArray(res?.list)
                ? res.list
                : [];
        setRequests(list);
        const pending = list.filter((r: DocumentAccessRequestItem) => r.status === "PENDING").length;
        if (onUpdateCount) onUpdateCount(pending);
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to fetch document access requests.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (show) {
      fetchRequests();
    }
  }, [show]);

  const handleRespond = (requestId: number, newStatus: "ACCEPTED" | "REJECTED" | "REVOKED") => {
    setActionLoading((prev) => ({ ...prev, [requestId]: true }));
    WebService.postAPI({
      action: "document-access/respond",
      body: {
        request_id: requestId,
        status: newStatus,
      },
    })
      .then((res: any) => {
        if (res?.status) {
          toast.success(res?.message || `Access request ${newStatus.toLowerCase()} successfully.`);
          fetchRequests();
        } else {
          toast.error(res?.message || `Failed to update access request.`);
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to update access request.");
      })
      .finally(() => {
        setActionLoading((prev) => ({ ...prev, [requestId]: false }));
      });
  };

  const filteredRequests = requests.filter((r) => {
    if (activeTab === "ALL") return true;
    if (activeTab === "PENDING") return r.status === "PENDING";
    if (activeTab === "ACCEPTED") return r.status === "ACCEPTED";
    if (activeTab === "REJECTED") return r.status === "REJECTED" || r.status === "REVOKED";
    return true;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ACCEPTED":
        return <span className="badge-access-granted" style={{ padding: "4px 12px", fontSize: "12px" }}>Access Granted</span>;
      case "PENDING":
        return <Badge bg="warning" text="dark">Pending</Badge>;
      case "REJECTED":
        return <Badge bg="danger">Rejected</Badge>;
      case "REVOKED":
        return <Badge bg="secondary">Revoked</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  const getUserTypeBadge = (item: any) => {
    const req = item.requester;
    const rawType = (
      item.user_type ||
      req?.user_type ||
      req?.login_type ||
      req?.account_type ||
      (item.is_mentor || item.is_request_from_mentor ? "Mentor" : "")
    ).toString().trim();

    if (!rawType) return null;

    const upper = rawType.toUpperCase();
    let bg = "secondary";
    let displayLabel = rawType;

    if (upper.includes("MENTOR")) {
      displayLabel = "Mentor";
      bg = "primary";
    } else if (upper.includes("INVESTOR")) {
      displayLabel = "Investor";
      bg = "info";
    } else if (upper.includes("ENTREPRENEUR") || upper.includes("ENTP") || upper.includes("WANT") || upper.includes("INVESTMENT")) {
      displayLabel = "Entrepreneur";
      bg = "success";
    }

    return (
      <Badge bg={bg} className="ms-2 font-11 px-2 py-1" style={{ borderRadius: "10px", fontWeight: 600 }}>
        {displayLabel}
      </Badge>
    );
  };

  const getRequesterName = (req?: DocumentAccessRequestItem["requester"]) => {
    if (!req) return "Unknown User";
    if (req.name && req.name.trim()) return req.name.trim();
    const name = `${req.first_name || ""} ${req.last_name || ""}`.trim();
    return name || req.email || `User #${req.id}`;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2 font-18 fw-bold">
          <FiShield className="text-primary" size={22} />
          Document Access Requests
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Nav variant="pills" activeKey={activeTab} onSelect={(k: any) => setActiveTab(k)}>
            <Nav.Item>
              <Nav.Link eventKey="PENDING" className="py-1 px-3">
                Pending ({requests.filter((r) => r.status === "PENDING").length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="ACCEPTED" className="py-1 px-3">
                Granted ({requests.filter((r) => r.status === "ACCEPTED").length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="REJECTED" className="py-1 px-3">
                Rejected / Revoked ({requests.filter((r) => r.status === "REJECTED" || r.status === "REVOKED").length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="ALL" className="py-1 px-3">
                All ({requests.length})
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Button variant="light" size="sm" onClick={fetchRequests} disabled={loading} title="Refresh Requests">
            <FiRefreshCw className={loading ? "spin" : ""} size={14} />
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="sm" />
            <span className="ms-2 text-muted">Loading access requests...</span>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-4 text-muted border rounded bg-light">
            <FiLock size={32} className="mb-2 text-secondary opacity-50" />
            <div>No document access requests found.</div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2" style={{ maxHeight: "420px", overflowY: "auto" }}>
            {filteredRequests.map((item) => {
              const name = getRequesterName(item.requester);
              const email = item.requester?.email;
              const dateStr = item.requested_at || item.created_at;
              const isBusy = !!actionLoading[item.id];

              return (
                <div key={item.id} className="p-3 border rounded d-flex flex-wrap align-items-center justify-content-between gap-2 bg-white">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle bg-light d-flex align-items-center justify-content-center fw-bold text-primary cursor-pointer"
                      style={{ width: "42px", height: "42px", fontSize: "16px", border: "1px solid #e0e0e0" }}
                      onClick={() => handleViewProfile(item)}
                      title="Click to review requesting user profile"
                    >
                      {name.charAt(0).toUpperCase() || <FiUser />}
                    </div>
                    <div>
                      <div
                        className="fw-semibold text-primary font-15 cursor-pointer d-flex align-items-center gap-1"
                        onClick={() => handleViewProfile(item)}
                        title="Click to review requesting user profile"
                      >
                        <span>{name}</span>
                        {getUserTypeBadge(item)}
                        <FiExternalLink size={14} className="ms-1" />
                      </div>
                      {email && <div className="text-muted font-13">{email}</div>}
                      <div className="text-muted font-12 d-flex align-items-center gap-1 mt-1">
                        <FiClock size={12} />
                        {dateStr ? moment(dateStr).format("MMM DD, YYYY hh:mm A") : "Requested"}
                        <span className="ms-2">{getStatusBadge(item.status)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 ms-auto">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleViewProfile(item)}
                      className="d-flex align-items-center gap-1"
                      title="Review Requesting User Profile"
                    >
                      <FiExternalLink size={14} /> Review Profile
                    </Button>
                    {item.status === "PENDING" && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          disabled={isBusy}
                          onClick={() => handleRespond(item.id, "ACCEPTED")}
                          className="d-flex align-items-center gap-1"
                        >
                          <FiCheck size={14} /> Accept
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={isBusy}
                          onClick={() => handleRespond(item.id, "REJECTED")}
                          className="d-flex align-items-center gap-1"
                        >
                          <FiX size={14} /> Reject
                        </Button>
                      </>
                    )}

                    {item.status === "ACCEPTED" && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => handleRespond(item.id, "REVOKED")}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiLock size={13} /> Revoke Access
                      </Button>
                    )}

                    {(item.status === "REJECTED" || item.status === "REVOKED") && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => handleRespond(item.id, "ACCEPTED")}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiCheck size={14} /> Allow Access
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentAccessRequestsModal;
